import { FC, useCallback, useMemo, useRef } from 'react';
import {
  Broadcast,
  BroadcastSortingField,
  Order,
  ProviderType,
  UserGroupEnum,
} from 'videoservice-openapi-react';
import {
  BroadcastFilter,
  MappedEventsCell,
  StatusCell,
  StreamCell,
  EditBroadcastModalContent,
} from 'widgets/broadcast';
import { ContentStyled } from 'widgets/page';
import { BroadcastPagination } from 'features/broadcast/broadcast-pagination';
import { useEvents } from 'features/event';
import { useMatchTypesForEvents } from 'features/match-type';
import {
  ProviderCell,
  useRowColorSelector,
  useBroadcastStore,
  IdCell,
  useFilterContext,
  usePaginatedBroadcastsFilter,
} from 'entities/broadcast';
import { useElementSize } from 'shared/lib/element';
import { useModalContext } from 'shared/lib/modal';
import { useHasRole } from 'shared/lib/role';
import { EElementSize } from 'shared/types';
import { Date } from 'shared/ui';
import { SportCell } from 'shared/ui/sport-cell';
import { Sticky } from 'shared/ui/sticky';
import { ESort, ISorting, TCellRender, Table } from 'shared/ui/table';
import { columns } from '../model';
import { FilterWrapperStyled, TableWrapperStyled } from './broadcasts.styled';

const cellRender: TCellRender<Broadcast> = {
  enabled: item => <StatusCell item={item} />,
  id: item => <IdCell streamId={item.streamId}></IdCell>,
  startDate: item => (
    <>
      {item.startDate && (
        <Date date={item.startDate} pattern="dd.MM.yyyy HH:mm" />
      )}
    </>
  ),
  providerType: item => <ProviderCell broadcast={item} />,
  sportId: item => <SportCell sportId={item.sportId} />,
  broadcastTournament: item => <>{item.broadcastTournament?.name}</>,
  streamId: item => <StreamCell broadcast={item} />,
  mappings: item => <MappedEventsCell broadcast={item} />,
};

export const BroadcastsContent: FC = () => {
  const { setSortBy, setOrder } = useFilterContext();
  const colorSelector = useRowColorSelector();
  const { openModal } = useModalContext();
  const { hasRole } = useHasRole();
  const broadcastList = useBroadcastStore(state => state.list);
  const { isLoading } = usePaginatedBroadcastsFilter();

  useEvents();
  useMatchTypesForEvents();

  const canEditBroadcast = useMemo(
    () => hasRole(UserGroupEnum.CUSTOMER),
    [hasRole],
  );

  const filterRef = useRef<HTMLDivElement>(null);
  const { height: filterHeight } = useElementSize(filterRef.current);
  const tableStickyOffset = useMemo(
    () => `${filterHeight + 32}px`,
    [filterHeight],
  );

  const onSort = useCallback(
    (sort: ISorting<BroadcastSortingField> | null) => {
      setSortBy(sort?.value);
      setOrder(
        sort?.mode === ESort.ASC
          ? Order.ASC
          : sort?.mode === ESort.DESC
            ? Order.DESC
            : undefined,
      );
    },
    [setOrder, setSortBy],
  );

  const rowClickHandler = useCallback(
    (item: Broadcast) => {
      if (canEditBroadcast && item.providerType === ProviderType.MANUAL) {
        openModal(<EditBroadcastModalContent broadcast={item} />);
      }
    },
    [canEditBroadcast, openModal],
  );

  return (
    <ContentStyled>
      <Sticky top="16px" id="broadcast-filter">
        <FilterWrapperStyled>
          <BroadcastFilter ref={filterRef} />
        </FilterWrapperStyled>
      </Sticky>
      <TableWrapperStyled>
        <Table<Broadcast, BroadcastSortingField>
          cellRender={cellRender}
          columns={columns}
          list={broadcastList}
          isSortable={true}
          onSort={onSort}
          onRowClick={rowClickHandler}
          isLoading={isLoading}
          rowColor={colorSelector}
          stickyOffset={tableStickyOffset}
          isSticky
        ></Table>
      </TableWrapperStyled>
      <BroadcastPagination openInNewTab={false} $size={EElementSize.M} />
    </ContentStyled>
  );
};
